
import { UseFormReturn } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { cn } from '@/lib/utils';
import { useApp } from '@/lib/context';
import { getTranslations } from '@/lib/translations';

interface Step1DateProps {
  form: UseFormReturn<any>;
}

export function Step1Date({ form }: Step1DateProps) {
  const { user } = useApp();
  const t = getTranslations(user?.language || 'en');

  // Calcola le date disponibili (oggi e i prossimi 2 giorni)
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 0; i < 3; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    
    return dates;
  };

  const availableDates = getAvailableDates();
  const selectedDate = form.watch('date');

  const formatDateLabel = (date: Date, index: number) => {
    if (index === 0) return t.speedDates.today;
    if (index === 1) return t.speedDates.tomorrow;
    return t.speedDates.dayAfterTomorrow;
  };

  const getGradientStyle = (index: number) => {
    if (index === 0) {
      return 'radial-gradient(circle at bottom right, rgba(227, 5, 38, 0.6) 0%, rgba(243, 244, 246, 0.05) 30%)';
    } else if (index === 1) {
      return 'radial-gradient(circle at bottom right, rgba(224, 132, 0, 0.8) 0%, rgba(243, 244, 246, 0.05) 30%)';
    } else {
      return 'radial-gradient(circle at bottom right, rgba(107, 229, 47, 0.8) 0%, rgba(243, 244, 246, 0.05) 30%)';
    }
  };

  const isDateSelected = (date: Date) => {
    if (!selectedDate) return false;
    return selectedDate.toDateString() === date.toDateString();
  };

  const formatDayMonth = (date: Date) => {
    const locale = user?.language === 'it' ? 'it-IT' : 
                   user?.language === 'es' ? 'es-ES' :
                   user?.language === 'de' ? 'de-DE' :
                   user?.language === 'pt' ? 'pt-PT' :
                   user?.language === 'fr' ? 'fr-FR' : 'en-US';
    
    const options: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' };
    return date.toLocaleDateString(locale, options);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-lg font-semibold tg-text mb-2">
          {user?.language === 'it' ? 'Quando sarà il tuo speed date?' : 
           user?.language === 'es' ? '¿Cuándo será tu speed date?' :
           user?.language === 'de' ? 'Wann findet dein Speed Date statt?' :
           user?.language === 'pt' ? 'Quando será o seu speed date?' :
           user?.language === 'fr' ? 'Quand aura lieu ton speed date ?' :
           'When is your speed date?'}
        </h2>
        <p className="text-sm tg-hint">
          {user?.language === 'it' ? 'Gli speed date possono essere creati solo per oggi o i prossimi 2 giorni' : 
           user?.language === 'es' ? 'Los speed dates solo se pueden crear para hoy o los próximos 2 días' :
           user?.language === 'de' ? 'Speed Dates können nur für heute oder die nächsten 2 Tage erstellt werden' :
           user?.language === 'pt' ? 'Speed dates só podem ser criados para hoje ou os próximos 2 dias' :
           user?.language === 'fr' ? 'Les speed dates ne peuvent être créés que pour aujourd\'hui ou les 2 prochains jours' :
           'Speed dates can only be created for today or the next 2 days'}
        </p>
      </div>

      <FormField
        control={form.control}
        name="date"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormControl>
              <div className="space-y-3">
                {availableDates.map((date, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => {
                      field.onChange(date);
                      // Haptic feedback
                      if (window.Telegram?.WebApp?.HapticFeedback) {
                        window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
                      }
                    }}
                    className={cn(
                      "w-full rounded-xl py-2 px-4 relative overflow-hidden transition-all duration-300",
                      isDateSelected(date)
                        ? "scale-[1.02] shadow-lg"
                        : "hover:scale-[1.01]"
                    )}
                    style={{
                      background: getGradientStyle(index)
                    }}
                  >
                    <div className="relative z-10 flex items-center justify-between">
                      <div className="text-left">
                        <div className="text-white text-xl font-bold mb-1">
                          {formatDateLabel(date, index)}
                        </div>
                        <div className="text-white/80 text-sm">
                          {formatDayMonth(date)}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="text-white text-3xl font-bold">
                          {date.getDate()}
                        </div>
                        
                        {isDateSelected(date) && (
                          <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center">
                            <svg
                              className="w-4 h-4 text-green-600"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="3"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path d="M5 13l4 4L19 7"></path>
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Background decoration */}
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-10 translate-x-10"></div>
                  </button>
                ))}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
